import React, { createContext, useContext, useEffect, useState } from "react";

import { authService, type User } from "~/lib/auth-service";

export interface AuthContextValue {
  isAuthenticated: boolean;
  isInitialized: boolean;
  isQaMode: boolean;
  user: User | null;
  logout: () => void;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

function buildAuthContextValue(): AuthContextValue {
  return {
    isAuthenticated: authService.isAuthenticated(),
    isInitialized: authService.isInitialized(),
    isQaMode: authService.isQaMode(),
    user: authService.user(),
    logout: () => authService.logout(),
  };
}

export type AuthProviderConfig = Partial<AuthContextValue>;

interface AuthProviderProps {
  // Optional overrides for the auth service for testing
  config?: AuthProviderConfig;
  children: React.ReactNode;
}

export const AuthProvider = ({ config = {}, children }: AuthProviderProps) => {
  const [authState, setAuthState] = useState<AuthContextValue>(
    buildAuthContextValue,
  );

  useEffect(() => {
    const unsubscribe = authService.subscribe(() => {
      setAuthState(buildAuthContextValue());
    });

    return unsubscribe;
  }, []);

  return (
    <AuthContext.Provider value={{ ...authState, ...config }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);

  if (!ctx) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return ctx;
};
